/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { startTransition, use, useState } from 'react';
import { saveUIState } from '@/stores/actions';
import { useAppState } from '@/stores/AppState';
import dynamic from 'next/dynamic';
import { getStoredInteractionLocation } from '../../utils/ymmeLocationHelper';
import { useAcesFlags } from '../../utils/acesFlags';
import { useHeaderData } from '@/features/header/api/getHeader';
import { YMMEInfoContext } from './context/YMMEInfoContext';
import { type CartProductVehicleAttributes } from '@/types/reduxStore/orders';
import { type VehicleProps } from '../types';

const LazyYMMEModalBody = dynamic(
  () => import('./YMMEModalBody').then((mod) => mod.YMMEModalBody),
  { ssr: false },
);

export type WrapperProps = {
  showYmme?: boolean;
  pageType?: string;
  closeYmmeWidget?: (vehicleSelected?: boolean) => void;
  partGroupId?: string;
  partGroupName?: string;
  bannerImage?: string;
  bannerAltText?: string;
  ymmeCaptionText?: string;
  ctaButtonText?: string;
  campaignFlow?: boolean;
  selectedMethod?: string;
  preselectedMakeId?: number;
  onUpdateVehicleSuccess?: () => void;
  onSelectVehicle?: (vehicle: VehicleProps) => void;
  cartProductVehicle?: CartProductVehicleAttributes;
  cartProductVehicleId?: string;
  onVehicleDeleted?: (vehicleModelId?: number) => void;
};

export function YMMEModal({
  pageType = '',
  showYmme = false,
  closeYmmeWidget,
  partGroupId,
  bannerImage,
  bannerAltText,
  ymmeCaptionText,
  ctaButtonText,
  campaignFlow = false,
  partGroupName = '',
  selectedMethod = '',
  preselectedMakeId,
  onUpdateVehicleSuccess,
  onSelectVehicle,
  cartProductVehicle,
  cartProductVehicleId,
  onVehicleDeleted,
}: WrapperProps) {
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const { acesEnabled } = useAcesFlags();
  const { dispatch: dispatchAppState } = useAppState();
  const interactionLocation = pageType ?? getStoredInteractionLocation();
  const { data: headerData } = useHeaderData();
  const hasVehicles = Number(headerData?.vehicleMap?.vehicleCount) > 0;
  const { isVehicleQuestionsYMMEOpen } = use(YMMEInfoContext);
  const closeYmme = (vehicleSelected?: boolean) => {
    startTransition(() => {
      setShowAddVehicle(false);
      closeYmmeWidget?.(vehicleSelected ?? false);
      dispatchAppState(saveUIState({ isYmmeTooltipEnabled: false }));
    });
  };

  if (showYmme || isVehicleQuestionsYMMEOpen) {
    return (
      <LazyYMMEModalBody
        showYmme={showYmme}
        pageType={interactionLocation}
        selectedMethod={selectedMethod}
        partGroupId={partGroupId}
        setShowAddVehicle={setShowAddVehicle}
        showAddVehicle={showAddVehicle}
        closeYmmeWidget={closeYmme}
        bannerImage={bannerImage}
        bannerAltText={bannerAltText}
        ymmeCaptionText={ymmeCaptionText}
        ctaButtonText={ctaButtonText}
        campaignFlow={campaignFlow}
        partGroupName={partGroupName}
        onUpdateVehicleSuccess={(isUpdateVehicleSuccess) => {
          setShowAddVehicle(false);
          if (acesEnabled && !isUpdateVehicleSuccess) {
            return;
          }
          onUpdateVehicleSuccess?.();
        }}
        hasVehicles={hasVehicles}
        preselectedMakeId={preselectedMakeId}
        cartProductVehicle={cartProductVehicle}
        cartProductVehicleId={cartProductVehicleId}
        onSelectVehicle={onSelectVehicle}
        onVehicleDeleted={onVehicleDeleted}
      />
    );
  }

  return null;
}
